import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { CheckCircle2, ClipboardList, Save } from "lucide-react";
import { AttachmentsSection } from "@/components/AttachmentsSection";

/**
 * FORMULÁRIO DE PRÉ-CONSULTA – NEURODIVERGÊNCIA (Telemedicina Multiprofissional)
 * 13 seções conforme documento oficial da Optia Saúde.
 * Caráter informativo — não substitui avaliação presencial.
 */
const SECTIONS = [
  { id: "identificacao", title: "1. Identificação da Criança", fields: [
    { key: "nome_completo", label: "Nome completo", type: "text" },
    { key: "data_nascimento", label: "Data de nascimento", type: "date" },
    { key: "idade", label: "Idade", type: "text" },
    { key: "sexo", label: "Sexo", type: "text" },
    { key: "nome_social", label: "Nome social (se houver)", type: "text" },
    { key: "cidade_estado", label: "Cidade / Estado", type: "text" },
    { key: "responsavel_preenchimento", label: "Responsável pelo preenchimento", type: "text" },
  ]},
  { id: "motivo", title: "2. Motivo da Busca", fields: [
    { key: "motivo_procura", label: "O que motivou a procura por atendimento neste momento?", type: "textarea" },
    { key: "expectativas_familia", label: "Expectativas da família", type: "textarea" },
  ]},
  { id: "gestacao", title: "3. Gestação e Nascimento", fields: [
    { key: "gestacao_planejada", label: "Gestação planejada", type: "text" },
    { key: "intercorrencias", label: "Intercorrências", type: "textarea" },
    { key: "tipo_parto", label: "Tipo de parto", type: "text" },
    { key: "idade_gestacional", label: "Idade gestacional", type: "text" },
    { key: "uti_neonatal", label: "UTI neonatal", type: "text" },
  ]},
  { id: "dnpm", title: "4. Desenvolvimento Neuropsicomotor", fields: [
    { key: "sentou", label: "Sentou (idade)", type: "text" },
    { key: "engatinhou", label: "Engatinhou (idade)", type: "text" },
    { key: "andou", label: "Andou (idade)", type: "text" },
    { key: "primeiras_palavras", label: "Primeiras palavras (idade)", type: "text" },
    { key: "frases_completas", label: "Frases completas (idade)", type: "text" },
    { key: "controle_esfincteriano", label: "Controle esfincteriano", type: "text" },
  ]},
  { id: "linguagem", title: "5. Comunicação e Linguagem", fields: [
    { key: "forma_comunicacao", label: "Forma principal de comunicação", type: "text" },
    { key: "compreensao_verbal", label: "Compreensão verbal", type: "text" },
    { key: "observacoes_ling", label: "Observações", type: "textarea" },
  ]},
  { id: "social", title: "6. Interação Social e Comportamento", fields: [
    { key: "relacao_adultos", label: "Relação com adultos", type: "textarea" },
    { key: "relacao_criancas", label: "Relação com crianças", type: "textarea" },
    { key: "comportamentos", label: "Comportamentos observados", type: "textarea" },
  ]},
  { id: "sensorial", title: "7. Perfil Sensorial", fields: [
    { key: "sensibilidades", label: "Sensibilidade a sons, luz, texturas, cheiros, sabores e movimento", type: "textarea" },
    { key: "sobrecarga_sensorial", label: "Situações de sobrecarga sensorial", type: "textarea" },
  ]},
  { id: "escola", title: "8. Escola e Aprendizagem", fields: [
    { key: "matriculada", label: "Está matriculada", type: "text" },
    { key: "tipo_escola", label: "Tipo de escola", type: "text" },
    { key: "serie", label: "Série / Ano", type: "text" },
    { key: "facilidades", label: "Facilidades", type: "textarea" },
    { key: "dificuldades", label: "Dificuldades", type: "textarea" },
    { key: "apoio_escolar", label: "Apoio escolar", type: "textarea" },
  ]},
  { id: "rotina", title: "9. Rotina Diária", fields: [
    { key: "sono", label: "Sono", type: "textarea" },
    { key: "alimentacao", label: "Alimentação", type: "textarea" },
    { key: "uso_telas", label: "Uso de telas", type: "text" },
    { key: "atividades_preferidas", label: "Atividades preferidas", type: "textarea" },
  ]},
  { id: "saude", title: "10. Saúde e Acompanhamentos", fields: [
    { key: "diagnosticos", label: "Diagnósticos prévios", type: "textarea" },
    { key: "medicacoes", label: "Medicações", type: "textarea" },
    { key: "alergias", label: "Alergias", type: "text" },
    { key: "acompanhamentos", label: "Acompanhamentos realizados", type: "textarea" },
  ]},
  { id: "familia", title: "11. Contexto Familiar", fields: [
    { key: "com_quem_mora", label: "Com quem mora", type: "textarea" },
    { key: "historico_familiar", label: "Histórico familiar relevante", type: "textarea" },
    { key: "forcas", label: "Forças e potencialidades da criança", type: "textarea" },
  ]},
  { id: "tele", title: "12. Telemedicina e Acessibilidade", fields: [
    { key: "tolera_video", label: "Tolera videochamadas", type: "text" },
    { key: "necessita_pausas", label: "Necessita pausas", type: "text" },
    { key: "adaptacoes", label: "Adaptações necessárias", type: "textarea" },
  ]},
];

const STORAGE_KEY = "optia_pre_anamnese_draft";

export default function PreAnamnesis() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({});
  const [consent, setConsent] = useState(false);
  const [consentName, setConsentName] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: srv } = await api.get("/pre-anamnesis/me");
        if (srv?.data && Object.keys(srv.data).length) {
          setData(srv.data);
          setConsent(!!srv.data.__consent);
          setConsentName(srv.data.__consent_name || "");
          setSavedAt(srv.updated_at);
        } else {
          const draft = localStorage.getItem(STORAGE_KEY);
          if (draft) setData(JSON.parse(draft));
        }
      } catch {}
      setLoading(false);
    })();
  }, []);

  const upd = (k, v) => setData((prev) => {
    const next = { ...prev, [k]: v };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    return next;
  });

  const saveDraft = async () => {
    setBusy(true);
    try {
      await api.put("/pre-anamnesis", { data, completed: false });
      toast.success("Rascunho salvo");
      setSavedAt(new Date().toISOString());
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const submit = async () => {
    if (!consent) { toast.error("É necessário aceitar a declaração de veracidade"); return; }
    if (!consentName.trim()) { toast.error("Informe o nome do responsável"); return; }
    setBusy(true);
    try {
      const withConsent = {
        ...data,
        __consent: true,
        __consent_name: consentName.trim(),
        __consent_at: new Date().toISOString(),
      };
      await api.put("/pre-anamnesis", { data: withConsent, completed: true });
      localStorage.removeItem(STORAGE_KEY);
      await refresh?.();
      toast.success("Ficha enviada! Agora você pode agendar consultas.");
      nav("/profissionais");
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  if (loading) return <div className="container-optia py-16 text-slate-500">Carregando…</div>;
  if (user && user.role !== "patient") return <div className="container-optia py-16 text-slate-500">Apenas pacientes podem preencher a ficha.</div>;

  const totalFields = SECTIONS.reduce((s, sec) => s + sec.fields.length, 0);
  const filled = SECTIONS.reduce((s, sec) => s + sec.fields.filter((f) => !!(data[f.key] || "").toString().trim()).length, 0);
  const progress = Math.round((filled / totalFields) * 100);

  return (
    <div className="container-optia py-10 animate-fade-in max-w-3xl">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-2xl bg-teal-100 grid place-items-center text-teal-700"><ClipboardList className="w-5 h-5" /></div>
        <div>
          <h1 className="font-display text-2xl font-semibold text-navy-900">Pré-consulta — Neurodivergência</h1>
          <p className="text-sm text-slate-600">Telemedicina multiprofissional — Optia Saúde</p>
        </div>
      </div>

      <div className="mt-5 rounded-xl bg-teal-50 border border-teal-200 p-4 text-sm text-teal-900">
        Este formulário tem <b>caráter informativo e preparatório</b>. Não substitui avaliação clínica presencial nem estabelece diagnóstico. As respostas ajudam nossa equipe a se preparar para a sua primeira consulta.
      </div>

      {/* progress */}
      <div className="mt-4 flex items-center gap-3">
        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-2 bg-teal-500 transition-all" style={{ width: `${progress}%` }} /></div>
        <div className="text-sm text-slate-600 w-16 text-right">{progress}%</div>
      </div>
      {savedAt && <p className="text-xs text-slate-500 mt-1">Última alteração: {new Date(savedAt).toLocaleString("pt-BR")}</p>}

      <div className="mt-6 space-y-5" data-testid="preana-form">
        {SECTIONS.map((sec) => (
          <Card key={sec.id} className="card-soft" data-testid={`preana-sec-${sec.id}`}>
            <CardContent className="p-6">
              <h2 className="font-display text-lg font-semibold text-navy-900">{sec.title}</h2>
              <div className="mt-4 grid gap-4">
                {sec.fields.map((f) => (
                  <div key={f.key}>
                    <Label className="text-sm">{f.label}</Label>
                    {f.type === "textarea" ? (
                      <Textarea rows={3} value={data[f.key] || ""} onChange={(e)=>upd(f.key, e.target.value)} data-testid={`preana-${f.key}`} />
                    ) : (
                      <Input type={f.type} value={data[f.key] || ""} onChange={(e)=>upd(f.key, e.target.value)} data-testid={`preana-${f.key}`} />
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Consentimento */}
        <Card className="card-soft border-teal-300" data-testid="preana-consent-card">
          <CardContent className="p-6">
            <h2 className="font-display text-lg font-semibold text-navy-900">13. Consentimento</h2>
            <label className="mt-3 flex items-start gap-3 cursor-pointer">
              <Checkbox checked={consent} onCheckedChange={(v)=>setConsent(!!v)} data-testid="preana-consent-check" />
              <span className="text-sm text-slate-700 leading-relaxed">Declaro que as informações acima são verdadeiras e <b>autorizo seu uso para avaliação multiprofissional</b> pela equipe da Optia Saúde.</span>
            </label>
            <div className="mt-4">
              <Label>Nome do responsável</Label>
              <Input value={consentName} onChange={(e)=>setConsentName(e.target.value)} placeholder="Nome completo" data-testid="preana-consent-name" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Anexos — exames, laudos e imagens */}
      {user?.id && (
        <div className="mt-5">
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900 mb-3">
            <b>Anexe seus exames (opcional).</b> Anexe aqui exames laboratoriais, laudos, receitas ou imagens que a equipe médica deva analisar antes da consulta. Formatos aceitos: <b>JPG, PNG ou PDF</b> — até <b>10 MB</b> por arquivo. Os arquivos são armazenados de forma privada e só ficam visíveis para você e a equipe assistencial.
          </div>
          <AttachmentsSection patientId={user.id} currentUser={user} />
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-3 justify-end sticky bottom-4 bg-white/90 backdrop-blur-md rounded-xl border border-slate-200 p-3 shadow-sm">
        <Button variant="outline" onClick={saveDraft} disabled={busy} data-testid="preana-save-draft"><Save className="w-4 h-4 mr-1" />Salvar rascunho</Button>
        <Button onClick={submit} disabled={busy || !consent || !consentName.trim()} className="bg-teal-500 hover:bg-teal-600" data-testid="preana-submit">
          <CheckCircle2 className="w-4 h-4 mr-1" />Enviar e continuar
        </Button>
      </div>
    </div>
  );
}
